import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { RevenueTrendDataPoint } from '../../../types/dashboard.types';
import type { TooltipProps } from 'recharts';
import styles from './Charts.module.css';
import { useTokenColors } from '../../../lib/useTokenColors';

// AB#2193 — Recharts writes series/axis colours into SVG attributes, so they are
// resolved from tokens via useTokenColors (and follow the theme). The legend dots in
// Charts.module.css use the same tokens.
const CHART_TOKENS = ['primary', 'accent-orchid-fg', 'text-secondary', 'chart-grid'] as const;

const tooltipContentStyle = {
  backgroundColor: 'var(--bg-elevated)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border-subtle)',
  borderRadius: '8px',
  padding: '8px 12px',
  fontSize: '12px',
};

// Animated number component for counter animation
interface AnimatedNumberProps {
  value: number;
  className?: string;
}

const AnimatedNumber: React.FC<AnimatedNumberProps> = ({ value, className }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const hasAnimatedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const lastValueRef = useRef<number>(value);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    // Reset animation state if value changed (e.g., filter update)
    if (lastValueRef.current !== value) {
      hasAnimatedRef.current = false;
      setDisplayValue(0);
      lastValueRef.current = value;
    }

    // If already animated for this value, skip
    if (hasAnimatedRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimatedRef.current) {
            hasAnimatedRef.current = true;
            const targetValue = value;
            const duration = 1500; // 1.5 seconds
            const startTime = performance.now();
            startTimeRef.current = startTime;

            const animate = (currentTime: number) => {
              if (!startTimeRef.current) return;

              const elapsed = currentTime - startTimeRef.current;
              const progress = Math.min(elapsed / duration, 1);

              // Easing function (ease-out)
              const easeOut = 1 - Math.pow(1 - progress, 3);
              const currentValue = Math.floor(easeOut * targetValue);

              setDisplayValue(currentValue);

              if (progress < 1) {
                animationFrameRef.current = requestAnimationFrame(animate);
              } else {
                setDisplayValue(targetValue);
              }
            };

            animationFrameRef.current = requestAnimationFrame(animate);
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
      }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [value]);

  return (
    <div ref={containerRef} className={className}>
      {displayValue.toLocaleString()}
    </div>
  );
};

// Custom tooltip that shows both Appointments and Amount values
const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  // Process all items in payload to show both Appointments and Amount
  const items = payload.map((item) => {
    const name = item.name || '';
    const value = item.value as number;
    let displayValue: string;
    
    if (name === 'Appointments') {
      // Value is already the raw appointment count
      displayValue = String(value);
    } else {
      // Value is already the raw revenue amount
      displayValue = `${Number(value).toLocaleString()} AED`;
    }
    
    return { name, displayValue, color: item.color };
  });

  return (
    <div style={tooltipContentStyle}>
      <p style={{ margin: 0, marginBottom: '4px', fontWeight: 600 }}>{label}</p>
      {items.map((item, index) => (
        <p key={index} style={{ margin: 0 }}>
          <span style={{ color: item.color }}>●</span> {item.name}: {item.displayValue}
        </p>
      ))}
    </div>
  );
};

interface RevenueTrendChartProps {
  data: RevenueTrendDataPoint[];
  loading?: boolean;
  error?: boolean;
  incomplete?: boolean;
  title?: string;
}

const RevenueTrendChart: React.FC<RevenueTrendChartProps> = ({
  data,
  loading = false,
  error = false,
  incomplete = false,
  title = 'Appointments & revenue',
}) => {
  const tc = useTokenColors(CHART_TOKENS);
  const BOOKINGS_COLOR = tc.primary;
  const AMOUNT_COLOR = tc['accent-orchid-fg'];
  // State for legend chip selection (both selected by default)
  const [showAppointments, setShowAppointments] = useState(true);
  const [showAmount, setShowAmount] = useState(true);

  // Calculate metrics from data
  const metrics = useMemo(() => {
    if (!data || data.length === 0) {
      return { totalRevenue: 0, totalAppointments: 0 };
    }

    const totalRevenue = data.reduce((sum, point) => sum + (point.revenue || 0), 0);
    const totalAppointments = data.reduce((sum, point) => sum + (point.bookings || 0), 0);

    return { totalRevenue, totalAppointments };
  }, [data]);

  // Format date labels and normalize data for display
  // Sort by date to ensure chronological order
  const formattedData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }
    return data
      .map((point) => {
        let displayLabel = point.label;
        if (point.date) {
          try {
            const date = new Date(point.date);
            const month = date.toLocaleDateString('en-US', { month: 'short' });
            const day = date.getDate();
            displayLabel = `${month} ${day}`;
          } catch (e) {
            // Fallback to original label if date parsing fails
          }
        }
        // Use raw values for both revenue and appointments
        // Each will use its own Y-axis scale
        return {
          ...point,
          displayLabel,
          revenue: point.revenue || 0,
          bookings: point.bookings || 0,
        };
      })
      .sort((a, b) => {
        // Sort by date (ascending - oldest to newest)
        if (a.date && b.date) {
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        }
        // If no date, maintain original order
        return 0;
      });
  }, [data]);

  // Calculate Y-axis configurations for dual axes
  // Left Y-axis: Revenue (AED) - formatted with K
  // Right Y-axis: Appointments (count) - raw numbers
  const revenueAxisConfig = useMemo(() => {
    if (!data || data.length === 0) {
      // #1183: Use reasonable small scale when no data, not 100K
      return {
        domain: [0, 100],
        ticks: [0, 20, 40, 60, 80, 100],
        tickFormatter: (value: number) => {
          if (value >= 1000000) {
            return `${(value / 1000000).toLocaleString()}M`;
          } else if (value >= 1000) {
            return `${(value / 1000).toLocaleString()}K`;
          }
          return `${value.toLocaleString()}`;
        }
      };
    }
    
    const maxRevenue = Math.max(...data.map((d) => d.revenue || 0));
    
    // Calculate nice rounded scale
    if (maxRevenue <= 100000) {
      const scale = Math.ceil(maxRevenue / 20000) * 20000 || 100000;
      const step = scale / 5;
      return {
        domain: [0, scale],
        ticks: [0, step, step * 2, step * 3, step * 4, scale],
        tickFormatter: (value: number) => {
          if (value >= 1000000) {
            return `${(value / 1000000).toLocaleString()}M`;
          } else if (value >= 1000) {
            return `${(value / 1000).toLocaleString()}K`;
          }
          return `${value.toLocaleString()}`;
        }
      };
    } else {
      const scale = Math.ceil(maxRevenue / 50000) * 50000;
      const step = scale / 5;
      return {
        domain: [0, scale],
        ticks: [0, step, step * 2, step * 3, step * 4, scale],
        tickFormatter: (value: number) => {
          if (value >= 1000000) {
            return `${(value / 1000000).toLocaleString()}M`;
          } else if (value >= 1000) {
            return `${(value / 1000).toLocaleString()}K`;
          }
          return `${value.toLocaleString()}`;
        }
      };
    }
  }, [data]);

  const appointmentsAxisConfig = useMemo(() => {
    if (!data || data.length === 0) {
      return { 
        domain: [0, 100], 
        ticks: [0, 20, 50, 80, 100],
        tickFormatter: (value: number) => String(value)
      };
    }
    
    const maxAppointments = Math.max(...data.map((d) => d.bookings || 0));
    
    // Calculate nice rounded scale
    if (maxAppointments <= 100) {
      return {
        domain: [0, 100],
        ticks: [0, 20, 50, 80, 100],
        tickFormatter: (value: number) => String(value)
      };
    } else {
      const scale = Math.ceil(maxAppointments / 20) * 20;
      const step = scale / 5;
      return {
        domain: [0, scale],
        ticks: [0, step, step * 2, step * 3, step * 4, scale],
        tickFormatter: (value: number) => String(value)
      };
    }
  }, [data]);

  if (loading) {
    return (
      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <h3 className={styles.chartTitle}>{title}</h3>
        </div>
        <div className={`${styles.chartContainer} ${styles.chartLoading}`}>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <h3 className={styles.chartTitle}>{title}</h3>
        </div>
        <div className={`${styles.chartContainer} ${styles.chartError}`}>
          Error generating graph. Please refresh the page.
        </div>
      </div>
    );
  }

  if (incomplete || !data || data.length === 0) {
    return (
      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <h3 className={styles.chartTitle}>{title}</h3>
        </div>
        <div className={`${styles.chartContainer} ${styles.chartIncomplete}`}>
          {!data || data.length === 0
            ? 'No data available'
            : 'Incomplete data. Graph may not be accurate'}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <h3 className={styles.chartTitle}>{title}</h3>
        <button type="button" className={styles.exportBtn} aria-label="Export" onClick={() => {
          if (!formattedData || formattedData.length === 0) return;
          const csvRows = ['Date,Revenue (AED),Appointments'];
          formattedData.forEach(d => csvRows.push(`${d.displayLabel},${d.revenue},${d.bookings}`));
          const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'revenue-trend.csv';
          a.click();
          URL.revokeObjectURL(url);
        }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Export
        </button>
      </div>
      
      <div className={styles.revenueMetrics}>
        <div className={styles.revenueMetricCard}>
          <div className={styles.revenueValue}>
            <AnimatedNumber value={metrics.totalRevenue} />
            <span className={styles.revenueCurrency}>AED</span>
          </div>
          <div className={styles.revenueLabel}>Total revenue</div>
        </div>
        <div className={styles.revenueMetricCard}>
          <div className={styles.revenueValue}>
            <AnimatedNumber value={metrics.totalAppointments} />
          </div>
          <div className={styles.revenueLabel}>Total appointments</div>
        </div>
        <div className={styles.chartLegend}>
          <button
            type="button"
            className={`${styles.legendItem} ${showAppointments ? styles.legendItemSelected : ''}`}
            onClick={() => setShowAppointments(!showAppointments)}
          >
            <span className={`${styles.legendDot} ${styles.legendDotBookings}`}></span>
            <span>Appointments</span>
          </button>
          <button
            type="button"
            className={`${styles.legendItem} ${showAmount ? styles.legendItemSelected : ''}`}
            onClick={() => setShowAmount(!showAmount)}
          >
            <span className={`${styles.legendDot} ${styles.legendDotAmount}`}></span>
            <span>Amount</span>
          </button>
        </div>
      </div>

      <div className={styles.chartContainerWrapper}>
        <div className={styles.chartContainer}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={formattedData}
              margin={{ top: 8, right: 50, left: 50, bottom: 25 }}
              barCategoryGap="20%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke={tc['chart-grid']} vertical={false} />
              <XAxis
                dataKey="displayLabel"
                tick={{ fill: tc['text-secondary'], fontSize: 10 }}
                angle={-45}
                textAnchor="end"
                axisLine={{ stroke: tc['chart-grid'] }}
                tickLine={false}
                interval={formattedData.length > 14 ? Math.floor(formattedData.length / 10) : 0}
                height={50}
                label={{ 
                  value: 'Date', 
                  position: 'insideBottom',
                  offset: -5,
                  style: { textAnchor: 'middle', fill: tc['text-secondary'], fontSize: 12 }
                }}
              />
              {/* Left Y-axis: Revenue (AED) */}
              <YAxis
                yAxisId="revenue"
                domain={revenueAxisConfig.domain}
                ticks={revenueAxisConfig.ticks}
                tick={{ fill: tc['text-secondary'], fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={revenueAxisConfig.tickFormatter}
                orientation="left"
                label={{ 
                  value: 'Revenue (AED)', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { textAnchor: 'middle', fill: tc['text-secondary'], fontSize: 12 }
                }}
              />
              {/* Right Y-axis: Appointments (count) */}
              <YAxis
                yAxisId="appointments"
                domain={appointmentsAxisConfig.domain}
                ticks={appointmentsAxisConfig.ticks}
                tick={{ fill: tc['text-secondary'], fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={appointmentsAxisConfig.tickFormatter}
                orientation="right"
                label={{ 
                  value: 'Appointments', 
                  angle: 90, 
                  position: 'insideRight',
                  style: { textAnchor: 'middle', fill: tc['text-secondary'], fontSize: 12 }
                }}
              />
              <Tooltip
                content={<CustomTooltip />}
                shared={true}
                cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }}
              />
              {showAmount && (
                <Bar
                  yAxisId="revenue"
                  dataKey="revenue"
                  name="Amount"
                  fill={AMOUNT_COLOR}
                  radius={[4, 4, 0, 0]}
                  barSize={10}
                />
              )}
              {showAppointments && (
                <Bar
                  yAxisId="appointments"
                  dataKey="bookings"
                  name="Appointments"
                  fill={BOOKINGS_COLOR}
                  radius={[4, 4, 0, 0]}
                  barSize={10}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default RevenueTrendChart;
